import React, { useEffect, useMemo } from "react";

import { useTranslation } from "react-i18next";

import { useMutation, useQuery } from "@apollo/client";

import clsx from "clsx";
import dayjs from "dayjs";
import { toast } from "react-toastify";
import DatePicker from "react-datepicker";
import Select from "react-select";
import { FormProvider, useForm, Controller } from "react-hook-form";

import {
  QUERY_CLIENT,
  QUERY_CLIENT_PREFERENCES,
  QUERY_LOCALE,
} from "../../../../config/graphql/query";
import { UPDATE_CLIENT_PREFERENCES } from "../../../../config/graphql/mutation";

import { PreferencesController } from "../../../../components/Preferences";

import { useClientId } from "../hooks/useClient";
import useHasRole from "../../../../lib/hooks/useHasRole";
import { useCurrentHasPlan } from "../../../../components/PlanBlock";
import { EmailPreferencesController } from "../../../../components/EmailPreferences";
import Input from "../../../../components/Input";
import { reactSelectCustomStyles } from "../../../Employees/Employee/Information";

type ValidityUnit = "minutes" | "hours";

type ValidityField = { value: string; unit: ValidityUnit };

type FieldValues = {
  preferences?: Omit<IClientPreferences, "autoCheckout"> & {
    autoCheckout: { enabled: boolean; time: Date };
  };
  // Display only: the server stores minutes, the unit is how they are shown.
  accessCodeValidity?: { before: ValidityField; after: ValidityField };
};

// The shared react-select look, joined to the number input on its left.
const validityUnitStyles = {
  ...reactSelectCustomStyles(false),
  control: (provided: any, state: any) => ({
    ...reactSelectCustomStyles(false).control(provided, state),
    borderTopLeftRadius: 0,
    borderBottomLeftRadius: 0,
  }),
};

const DEFAULT_VALID_BEFORE_MINUTES = 120;
const DEFAULT_VALID_AFTER_MINUTES = 240;

const toValidityField = (minutes?: number | null): ValidityField => {
  if (typeof minutes !== "number") {
    return { value: "", unit: "hours" };
  }

  return minutes > 0 && minutes % 60 === 0
    ? { value: String(minutes / 60), unit: "hours" }
    : { value: String(minutes), unit: "minutes" };
};

// An empty field means the default, never "2" (it used to save 2h for "after").
const toMinutes = (field: ValidityField | undefined, fallback: number) => {
  const value = parseInt(field?.value ?? "", 10);

  if (Number.isNaN(value)) {
    return fallback;
  }

  return field?.unit === "hours" ? value * 60 : value;
};

const ClientPreferences = React.memo(() => {
  const { t } = useTranslation(["employeePreferences"]);

  const clientId = useClientId();

  const isAdmin = useHasRole(["ADMIN"]);
  const isClientAdmin = useHasRole(["CLIENT_ADMIN"]);

  const { data: localeData } = useQuery<{ locale: string }>(QUERY_LOCALE);
  const currentLocale = localeData?.locale ?? "en";

  const { data: clientData } = useQuery<{
    client: { preferences: IClientPreferences };
  }>(QUERY_CLIENT_PREFERENCES, {
    skip: !clientId,
    variables: { id: clientId },
    context: {
      headers: {
        ClientId: clientId,
      },
    },
    fetchPolicy: "network-only",
  });

  const isFree = useCurrentHasPlan(["free"]);

  const [onUpdate] = useMutation<{
    updateCompanyPreferences: IClientPreferences;
  }>(UPDATE_CLIENT_PREFERENCES, {
    context: {
      headers: {
        ClientId: clientId,
      },
    },
    refetchQueries: [
      {
        query: QUERY_CLIENT,
        variables: {
          id: clientId,
        },
      },
    ],
  });

  const methods = useForm<FieldValues>({
    shouldFocusError: false,
    shouldUnregister: true,
  });

  const validityDisabled = isFree || !(isAdmin || isClientAdmin);

  const validityUnitOptions = useMemo(
    () =>
      (["minutes", "hours"] as const).map((unit) => ({
        value: unit,
        label: t(`meetingPreferences:accessCodeValidity.unit.${unit}`),
      })),
    [t],
  );

  const autoCheckoutEnabled = methods.watch("preferences.autoCheckout.enabled");
  const hostReminderEnabled = methods.watch(
    "preferences.email.meeting.hostReminder.enabled",
  );
  const visitorReminderEnabled = methods.watch(
    "preferences.email.meeting.visitorReminder.enabled",
  );

  useEffect(() => {
    if (clientData?.client) {
      const autoCheckout = clientData?.client?.preferences?.autoCheckout;
      const existingCustomFields =
        clientData?.client?.preferences?.employeeCustomFields;

      const fields = existingCustomFields?.fields?.length
        ? existingCustomFields.fields
        : [
            {
              key: "field_1",
              label: "",
              show: true,
              required: false,
              duplicateCheck: false,
            },
          ];

      const employeeCustomFields = {
        enabled: !!existingCustomFields?.enabled,
        fields,
      };

      methods.reset({
        preferences: {
          ...clientData?.client?.preferences,
          autoCheckout: {
            enabled: autoCheckout?.enabled || false,
            time: autoCheckout?.time
              ? dayjs()
                  .set("hour", parseInt(autoCheckout.time.split(":")[0], 10)) // Set the hour
                  .set("minute", parseInt(autoCheckout.time.split(":")[1], 10))
                  .toDate()
              : undefined,
          },
          employeeCustomFields,
        },
        accessCodeValidity: {
          before: toValidityField(
            clientData?.client?.preferences?.meeting?.accessCodeValidity
              ?.validBeforeMinutes,
          ),
          after: toValidityField(
            clientData?.client?.preferences?.meeting?.accessCodeValidity
              ?.validAfterMinutes,
          ),
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientData]);

  const onSubmit = async (variables: FieldValues) => {
    const autoCheckout = variables.preferences?.autoCheckout;
    const reminderMinutesBefore = parseInt(
      variables.preferences?.email?.meeting?.reminderMinutesBefore || "10",
      10,
    );

    const validBeforeMinutes = toMinutes(
      variables.accessCodeValidity?.before,
      DEFAULT_VALID_BEFORE_MINUTES,
    );

    const validAfterMinutes = toMinutes(
      variables.accessCodeValidity?.after,
      DEFAULT_VALID_AFTER_MINUTES,
    );

    const employeeCustomFields = {
      enabled: !!variables.preferences?.employeeCustomFields?.enabled,
      fields: (variables.preferences?.employeeCustomFields?.fields || []).map(
        (field, index) => ({
          key: field?.key || `field_${index + 1}`,
          label: typeof field?.label === "string" ? field.label : "",
          language: currentLocale,
          show: field?.show !== false,
          required: !!field?.required,
          duplicateCheck: !!field?.duplicateCheck,
        }),
      ),
    };

    return onUpdate({
      variables: {
        input: {
          id: clientId,
          preferences: {
            ...variables.preferences,
            autoCheckout: {
              enabled: autoCheckout?.enabled || false,
              time: autoCheckout?.time
                ? dayjs(autoCheckout.time).format("HH:mm")
                : undefined,
            },
            email: {
              ...variables.preferences?.email,
              meeting: {
                ...variables.preferences?.email?.meeting,
                reminderMinutesBefore,
              },
            },
            meeting: {
              ...variables.preferences?.meeting,
              accessCodeValidity: {
                validBeforeMinutes,
                validAfterMinutes,
              },
              notifyHostOnEveryCheckin:
                !!variables.preferences?.meeting?.notifyHostOnEveryCheckin,
            },
            employeeCustomFields,
          },
        },
      },
    })
      .then(() => {
        toast.success<string>(t("employeePreferences:toast.preferencesUpdate"));
      })
      .catch((error) => {
        toast.error<string>(
          error.message ||
            t("employeePreferences:toast.preferencesUpdateError"),
        );
      });
  };

  return (
    <>
      <FormProvider {...methods}>
        <form onSubmit={methods.handleSubmit(onSubmit)}>
          <div className="row">
            <div className="col-xl-6">
              <div className="mb-4">
                <div>
                  <h5>{t(`employeePreferences:availability.title`)}</h5>
                </div>
                <p className="text-primary mb-2">
                  {t(`employeePreferences:availability.subtitle`)}
                </p>
                <div className="row">
                  <PreferencesController
                    name="preferences.company.employee.visitor"
                    label={t(
                      `employeePreferences:input.label.preferences.visitor.enabled`,
                    )}
                    disabled={!(isAdmin || isClientAdmin)}
                    autoDisabled={!(isAdmin || isClientAdmin)}
                  />
                  <PreferencesController
                    name="preferences.company.employee.meetingAttendee"
                    label={t(
                      `employeePreferences:input.label.preferences.meetingAttendee.enabled`,
                    )}
                    disabled={!(isAdmin || isClientAdmin)}
                    autoDisabled={!(isAdmin || isClientAdmin)}
                  />
                </div>
                <div className="row">
                  <PreferencesController
                    name="preferences.company.employee.foodDelivery"
                    label={t(
                      `employeePreferences:input.label.preferences.foodDelivery.enabled`,
                    )}
                    disabled={!(isAdmin || isClientAdmin)}
                    autoDisabled={!(isAdmin || isClientAdmin)}
                  />
                  <PreferencesController
                    name="preferences.company.employee.packageDelivery"
                    label={t(
                      `employeePreferences:input.label.preferences.packageDelivery.enabled`,
                    )}
                    disabled={!(isAdmin || isClientAdmin)}
                    autoDisabled={!(isAdmin || isClientAdmin)}
                  />
                </div>
              </div>
              <div className="row">
                <div className="col-6 mb-4">
                  <div>
                    <h5>{t(`autoCheckoutPreferences:title`)}</h5>
                  </div>
                  <p className="mb-3 text-primary">
                    {t(`autoCheckoutPreferences:subtitle`)}
                  </p>
                  <Controller
                    name="preferences.autoCheckout.enabled"
                    render={({
                      field: { value, name, onChange },
                      fieldState: { error },
                    }) => (
                      <div className="form-group form-check mb-2">
                        <input
                          id={name}
                          type="checkbox"
                          className="form-check-input"
                          checked={value}
                          onChange={onChange}
                          disabled={isFree || !(isAdmin || isClientAdmin)}
                        />
                        <label
                          className="form-check-label user-select-none"
                          htmlFor={name}
                        >
                          {t(`autoCheckoutPreferences:input.label.enabled`)}
                        </label>
                        {!!error && (
                          <div className="invalid-feedback">
                            {error.message}
                          </div>
                        )}
                      </div>
                    )}
                  />
                  {autoCheckoutEnabled && (
                    <Controller
                      name="preferences.autoCheckout.time"
                      render={({
                        field: { onChange, name, value },
                        fieldState: { error },
                      }) => {
                        return (
                          <div className="d-flex align-items-center">
                            <label
                              className="form-check-label user-select-none mr-2"
                              htmlFor={name}
                            >
                              {t(`autoCheckoutPreferences:input.label.time`)}
                            </label>
                            <div
                              style={{ width: "6rem" }}
                              className={clsx("", {
                                "is-invalid": !!error,
                              })}
                            >
                              <DatePicker
                                selected={value}
                                onChange={onChange}
                                showTimeSelect
                                showTimeSelectOnly
                                timeIntervals={15}
                                timeCaption="Time"
                                dateFormat="h:mm aa"
                                timeFormat="hh:mm aa"
                                disabled={isFree || !(isAdmin || isClientAdmin)}
                                className={clsx("form-control", {
                                  "is-invalid": !!error,
                                })}
                              />
                            </div>
                            <div className="invalid-feedback">
                              {error?.message ?? "Invalid"}
                            </div>
                          </div>
                        );
                      }}
                    />
                  )}
                </div>
                <div className="col-6 mb-4">
                  <div>
                    <h5>{t("cmsCheckoutPreferences:title")}</h5>
                  </div>
                  <p className="mb-3 text-primary">
                    {t("cmsCheckoutPreferences:subtitle")}
                  </p>
                  <Controller
                    name="preferences.cmsCheckout.enabled"
                    render={({
                      field: { value, name, onChange },
                      fieldState: { error },
                    }) => (
                      <div className="form-group form-check mb-2">
                        <input
                          id={name}
                          type="checkbox"
                          className="form-check-input"
                          checked={value}
                          onChange={onChange}
                          disabled={isFree || !(isAdmin || isClientAdmin)}
                        />
                        <label
                          className="form-check-label user-select-none"
                          htmlFor={name}
                        >
                          {t(`cmsCheckoutPreferences:input.label.enabled`)}
                        </label>
                        {!!error && (
                          <div className="invalid-feedback">
                            {error.message}
                          </div>
                        )}
                      </div>
                    )}
                  />
                </div>
              </div>
              <div className="row">
                <div className="col-6 mb-3">
                  <div>
                    <h5>{t("photoCapture:title")}</h5>
                  </div>
                  <p className="mb-2 text-primary">
                    {t("photoCapture:subtitle")}
                  </p>
                  <Controller
                    name="preferences.photoCapture.enabled"
                    render={({
                      field: { value, name, onChange },
                      fieldState: { error },
                    }) => (
                      <div className="form-group form-check mb-2">
                        <input
                          id={name}
                          type="checkbox"
                          className="form-check-input"
                          checked={value}
                          onChange={onChange}
                          disabled={isFree || !(isAdmin || isClientAdmin)}
                        />
                        <label
                          className="form-check-label user-select-none"
                          htmlFor={name}
                        >
                          {t(`photoCapture:input.label.enabled`)}
                        </label>
                        {!!error && (
                          <div className="invalid-feedback">
                            {error.message}
                          </div>
                        )}
                      </div>
                    )}
                  />
                </div>

                <div className="col-6 mb-4">
                  <div>
                    <h5>{t("employeePreferences:customField.title")}</h5>
                  </div>
                  <p className="mb-2 text-primary">
                    {t("employeePreferences:customField.subtitle")}
                  </p>
                  <Controller
                    name="preferences.employeeCustomFields.enabled"
                    render={({
                      field: { value, name, onChange },
                      fieldState: { error },
                    }) => (
                      <div className="form-group form-check mb-2">
                        <input
                          id={name}
                          type="checkbox"
                          className="form-check-input"
                          checked={!!value}
                          onChange={onChange}
                          disabled={!(isAdmin || isClientAdmin)}
                        />
                        <label
                          className="form-check-label user-select-none"
                          htmlFor={name}
                        >
                          {t(
                            "employeePreferences:customField.input.label.enabled",
                          )}
                        </label>
                        {!!error && (
                          <div className="invalid-feedback">
                            {error.message}
                          </div>
                        )}
                      </div>
                    )}
                  />
                  {!!methods.watch(
                    "preferences.employeeCustomFields.enabled",
                  ) && (
                    <>
                      <div className="form-group pl-0">
                        <label htmlFor="preferences.employeeCustomFields.fields.0.label">
                          {t(
                            "employeePreferences:customField.input.label.label",
                          )}
                        </label>
                        <Input.Translatable
                          name="preferences.employeeCustomFields.fields.0.label"
                          className="form-control"
                          placeholder={t(
                            "employeePreferences:customField.input.placeholder.label",
                          )}
                          disabled={!(isAdmin || isClientAdmin)}
                        />
                      </div>
                      <Controller
                        name="preferences.employeeCustomFields.fields.0.show"
                        render={({
                          field: { value, name, onChange },
                          fieldState: { error },
                        }) => (
                          <div className="form-group form-check mb-2">
                            <input
                              id={name}
                              type="checkbox"
                              className="form-check-input"
                              checked={value !== false}
                              onChange={onChange}
                              disabled={!(isAdmin || isClientAdmin)}
                            />
                            <label
                              className="form-check-label user-select-none"
                              htmlFor={name}
                            >
                              {t(
                                "employeePreferences:customField.input.label.show",
                              )}
                            </label>
                            {!!error && (
                              <div className="invalid-feedback">
                                {error.message}
                              </div>
                            )}
                          </div>
                        )}
                      />
                      <Controller
                        name="preferences.employeeCustomFields.fields.0.required"
                        render={({
                          field: { value, name, onChange },
                          fieldState: { error },
                        }) => (
                          <div className="form-group form-check mb-2">
                            <input
                              id={name}
                              type="checkbox"
                              className="form-check-input"
                              checked={!!value}
                              onChange={onChange}
                              disabled={!(isAdmin || isClientAdmin)}
                            />
                            <label
                              className="form-check-label user-select-none"
                              htmlFor={name}
                            >
                              {t(
                                "employeePreferences:customField.input.label.required",
                              )}
                            </label>
                            {!!error && (
                              <div className="invalid-feedback">
                                {error.message}
                              </div>
                            )}
                          </div>
                        )}
                      />
                      <Controller
                        name="preferences.employeeCustomFields.fields.0.duplicateCheck"
                        render={({
                          field: { value, name, onChange },
                          fieldState: { error },
                        }) => (
                          <div className="form-group form-check mb-2">
                            <input
                              id={name}
                              type="checkbox"
                              className="form-check-input"
                              checked={!!value}
                              onChange={onChange}
                              disabled={!(isAdmin || isClientAdmin)}
                            />
                            <label
                              className="form-check-label user-select-none"
                              htmlFor={name}
                            >
                              {t(
                                "employeePreferences:customField.input.label.duplicateCheck",
                              )}
                            </label>
                            {!!error && (
                              <div className="invalid-feedback">
                                {error.message}
                              </div>
                            )}
                          </div>
                        )}
                      />
                    </>
                  )}
                </div>
              </div>
            </div>

            <div className="col-xl-6">
              <div className="row">
                <div className="col-6">
                  <div>
                    <h5>
                      {t(`employeePreferences:accessCodeGeneration.title`)}
                    </h5>
                    <p className="mb-2 text-primary">
                      {t(
                        `employeePreferences:input.label.preferences.access.employeeHint`,
                      )}
                    </p>
                  </div>
                  <Controller
                    name="preferences.memberAccess.employee.enabled"
                    render={({
                      field: { value, name, onChange },
                      fieldState: { error },
                    }) => (
                      <div className="form-group form-check mb-2">
                        <input
                          id={name}
                          type="checkbox"
                          className="form-check-input"
                          checked={value}
                          onChange={onChange}
                          disabled={isFree || !(isAdmin || isClientAdmin)}
                        />
                        <label
                          className="form-check-label user-select-none"
                          htmlFor={name}
                        >
                          {t(
                            `employeePreferences:input.label.preferences.access.employee`,
                          )}
                        </label>
                        {!!error && (
                          <div className="invalid-feedback">
                            {error.message}
                          </div>
                        )}
                      </div>
                    )}
                  />
                  <Controller
                    name="preferences.memberAccess.visitor.enabled"
                    render={({
                      field: { value, name, onChange },
                      fieldState: { error },
                    }) => (
                      <div className="form-group form-check mb-4">
                        <input
                          id={name}
                          type="checkbox"
                          className="form-check-input"
                          checked={value}
                          onChange={onChange}
                          disabled={isFree || !(isAdmin || isClientAdmin)}
                        />
                        <label
                          className="form-check-label user-select-none"
                          htmlFor={name}
                        >
                          {t(
                            `employeePreferences:input.label.preferences.access.visitor`,
                          )}
                        </label>
                        {!!error && (
                          <div className="invalid-feedback">
                            {error.message}
                          </div>
                        )}
                      </div>
                    )}
                  />
                </div>
                <div className="col-6">
                  <div>
                    <h5>{t(`nfcAccess:title`)}</h5>
                    <p className="mb-2 text-primary">
                      {t(`nfcAccess:subtitle`)}
                    </p>
                  </div>
                  <Controller
                    name="preferences.nfc.enabled"
                    render={({
                      field: { value, name, onChange },
                      fieldState: { error },
                    }) => (
                      <div className="form-group form-check mb-2">
                        <input
                          id={name}
                          type="checkbox"
                          className="form-check-input"
                          checked={value}
                          onChange={onChange}
                          disabled={isFree || !(isAdmin || isClientAdmin)}
                        />
                        <label
                          className="form-check-label user-select-none"
                          htmlFor={name}
                        >
                          {t(`nfcAccess:input.label.enabled`)}
                        </label>
                        {!!error && (
                          <div className="invalid-feedback">
                            {error.message}
                          </div>
                        )}
                      </div>
                    )}
                  />
                </div>
              </div>
              <div className="mb-4">
                <h5 className="mb-3">{t(`emailPreferences:title`)}</h5>
                <div className="row">
                  <div className="col-6 mb-3">
                    <h6 className="mb-2">
                      {t(`emailPreferences:subtitle.checkin`)}
                    </h6>
                    <EmailPreferencesController
                      name="preferences.email.checkin.employee.enabled"
                      label={t(
                        `emailPreferences:input.label.preferences.email.checkin.employee`,
                      )}
                      disabled={!(isAdmin || isClientAdmin)}
                    />
                    <EmailPreferencesController
                      name="preferences.email.checkin.visitor.enabled"
                      label={t(
                        `emailPreferences:input.label.preferences.email.checkin.visitor`,
                      )}
                      disabled={!(isAdmin || isClientAdmin)}
                    />
                    <EmailPreferencesController
                      name="preferences.email.checkin.meetingAttendee.enabled"
                      label={t(
                        `emailPreferences:input.label.preferences.email.checkin.meetingAttendee`,
                      )}
                      disabled={!(isAdmin || isClientAdmin)}
                    />
                  </div>
                  <div className="col-6 mb-3">
                    <h6 className="mb-2">
                      {t(`emailPreferences:subtitle.checkout`)}
                    </h6>
                    <EmailPreferencesController
                      name="preferences.email.checkout.employee.enabled"
                      label={t(
                        `emailPreferences:input.label.preferences.email.checkout.employee`,
                      )}
                      disabled={!(isAdmin || isClientAdmin)}
                    />
                    <EmailPreferencesController
                      name="preferences.email.checkout.visitor.enabled"
                      label={t(
                        `emailPreferences:input.label.preferences.email.checkout.visitor`,
                      )}
                      disabled={!(isAdmin || isClientAdmin)}
                    />
                    <EmailPreferencesController
                      name="preferences.email.checkout.meetingAttendee.enabled"
                      label={t(
                        `emailPreferences:input.label.preferences.email.checkout.meetingAttendee`,
                      )}
                      disabled={!(isAdmin || isClientAdmin)}
                    />
                  </div>
                </div>
                <div className="mb-3">
                  <h6 className="mb-2">
                    {t(`emailPreferences:subtitle.meeting`)}
                  </h6>
                  {/* the minutes apply to both reminders, so they sit centred
                      against the pair rather than next to one of them */}
                  <div className="d-flex align-items-center">
                    <div>
                      <EmailPreferencesController
                        name="preferences.email.meeting.hostReminder.enabled"
                        label={t(
                          "emailPreferences:input.label.preferences.email.meeting.hostReminder",
                        )}
                        disabled={!(isAdmin || isClientAdmin)}
                      />
                      <EmailPreferencesController
                        name="preferences.email.meeting.visitorReminder.enabled"
                        label={t(
                          "emailPreferences:input.label.preferences.email.meeting.visitorReminder",
                        )}
                        disabled={!(isAdmin || isClientAdmin)}
                      />
                    </div>
                    {(hostReminderEnabled || visitorReminderEnabled) && (
                      <div
                        className="d-flex align-items-center ml-2"
                        style={{ transform: "translateY(-0.25rem)" }}
                      >
                        <Input
                          name="preferences.email.meeting.reminderMinutesBefore"
                          type="number"
                          disabled={isFree || !(isAdmin || isClientAdmin)}
                          min={1}
                          step={1}
                          style={{
                            width: "5rem",
                          }}
                        />
                        <span className="ml-1">
                          {t(
                            "emailPreferences:input.label.preferences.email.meeting.reminderMinutesBefore",
                          )}
                        </span>
                      </div>
                    )}
                  </div>

                  <EmailPreferencesController
                    name="preferences.email.meeting.hostConfirmation.enabled"
                    label={t(
                      `emailPreferences:input.label.preferences.email.meeting.hostConfirmation`,
                    )}
                    disabled={!(isAdmin || isClientAdmin)}
                  />
                  <EmailPreferencesController
                    name="preferences.email.meeting.hostCancellation.enabled"
                    label={t(
                      `emailPreferences:input.label.preferences.email.meeting.hostCancellation`,
                    )}
                    disabled={!(isAdmin || isClientAdmin)}
                  />
                  <EmailPreferencesController
                    name="preferences.email.meeting.visitorCancellation.enabled"
                    label={t(
                      `emailPreferences:input.label.preferences.email.meeting.visitorCancellation`,
                    )}
                    disabled={!(isAdmin || isClientAdmin)}
                  />
                  <EmailPreferencesController
                    name="preferences.email.meeting.internalEmployee.enabled"
                    label={t(
                      `emailPreferences:input.label.preferences.email.meeting.internalEmployee`,
                    )}
                    disabled={!(isAdmin || isClientAdmin)}
                  />
                  <div className="form-group mt-4 mb-1 ml-1">
                    <label className="d-block mb-2">
                      {t("meetingPreferences:accessCodeValidity.title")}
                    </label>
                    <div className="d-flex flex-wrap">
                      {(["before", "after"] as const).map((side) => (
                        <div key={side} className="mr-4 mb-2">
                          <label
                            htmlFor={`accessCodeValidity-${side}`}
                            className="small text-muted mb-1"
                          >
                            {t(`meetingPreferences:accessCodeValidity.${side}`)}
                          </label>
                          {/* Number and unit read as one control, like a Bootstrap input group */}
                          <div className="d-flex">
                            <div style={{ width: "6rem" }}>
                              <Input
                                id={`accessCodeValidity-${side}`}
                                name={`accessCodeValidity.${side}.value`}
                                type="number"
                                disabled={validityDisabled}
                                min={0}
                                // No `max`: browser validation would block saving the
                                // whole page for a legacy value above the server's
                                // 7-day limit, which the server lets through unchanged.
                                step={1}
                                style={{
                                  borderTopRightRadius: 0,
                                  borderBottomRightRadius: 0,
                                }}
                              />
                            </div>
                            <div style={{ width: "9rem", marginLeft: -1 }}>
                              <Controller
                                name={`accessCodeValidity.${side}.unit`}
                                render={({ field: { value, onChange } }) => (
                                  <Select
                                    inputId={`accessCodeValidity-${side}-unit`}
                                    isSearchable={false}
                                    isDisabled={validityDisabled}
                                    options={validityUnitOptions}
                                    value={validityUnitOptions.find(
                                      (option) => option.value === value,
                                    )}
                                    onChange={(option) =>
                                      onChange(option?.value)
                                    }
                                    styles={validityUnitStyles}
                                  />
                                )}
                              />
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                    <small className="text-muted d-block">
                      {t("meetingPreferences:accessCodeValidity.description")}
                    </small>
                  </div>
                  <Controller
                    name="preferences.meeting.notifyHostOnEveryCheckin"
                    render={({
                      field: { value, name, onChange },
                      fieldState: { error },
                    }) => (
                      <div className="form-group form-check mt-3 mb-1">
                        <input
                          id={name}
                          type="checkbox"
                          className="form-check-input"
                          checked={!!value}
                          onChange={onChange}
                          disabled={isFree || !(isAdmin || isClientAdmin)}
                        />
                        <label
                          className="form-check-label user-select-none"
                          htmlFor={name}
                        >
                          {t(
                            "meetingPreferences:notifyHostOnEveryCheckin.label",
                          )}
                        </label>
                        {!!error && (
                          <div className="invalid-feedback">
                            {error.message}
                          </div>
                        )}
                      </div>
                    )}
                  />
                  <small className="text-muted d-block ml-1">
                    {t(
                      "meetingPreferences:notifyHostOnEveryCheckin.description",
                    )}
                  </small>
                </div>
              </div>
            </div>
          </div>
          <div className="row">
            <div className="col-12">
              <button
                type="submit"
                className="btn btn-primary mt-2 mb-3"
                disabled={
                  methods.formState.isSubmitting || !(isAdmin || isClientAdmin)
                }
              >
                {t("employeePreferences:button.submit")}
              </button>
            </div>
          </div>
        </form>
      </FormProvider>
    </>
  );
});

export default ClientPreferences;
